/**
 * Forma de onda dos tons de ligação — o "tu… tu…" de quem disca e os três
 * bipes do fim. Função pura (sem WebAudio): é testada no vitest, e quem toca é
 * `services/ligacoes/tons-de-ligacao`.
 *
 * Ninguém manda esses sons pela rede: o protocolo do WhatsApp não tem "early
 * media", e o gateway só entrega silêncio enquanto a ligação toca. No WhatsApp
 * quem toca o "chamando" é o aparelho de quem liga — aqui, o navegador.
 *
 * Mesma cadência, frequência e volume do app (`GeradorDeTom.kt`/`.swift` no
 * whiz-mobile), para a ligação soar igual nos dois.
 */

/** 425 Hz: o tom de controle da telefonia brasileira (Anatel). */
export const FREQUENCIA_HZ = 425;
/** ~-14 dBFS: audível sem disputar com a voz que vier junto. */
const AMPLITUDE = 6_500 / 32_768;
const RAMPA_MS = 8;

export type Tom = 'chamando' | 'fim';

/** Pares (ligado, desligado) em ms. O "chamando" repete; o fim toca uma vez. */
export const CADENCIA: Record<Tom, readonly number[]> = {
    chamando: [1_000, 4_000],
    fim: [160, 110, 160, 110, 160],
};

export const DURACAO_MS: Record<Tom, number> = {
    chamando: CADENCIA.chamando.reduce((a, b) => a + b, 0),
    fim: CADENCIA.fim.reduce((a, b) => a + b, 0),
};

/**
 * Um ciclo do tom em amostras float (-1..1). O "chamando" é um ciclo de 5 s
 * para tocar em laço; o fim é o som inteiro.
 */
export function amostrasDoTom(tom: Tom, taxa: number): Float32Array {
    const passos = CADENCIA[tom].map(ms => Math.round(taxa * ms / 1000));
    const total = passos.reduce((a, b) => a + b, 0);
    const saida = new Float32Array(total);
    const rampa = taxa * RAMPA_MS / 1000;

    let inicio = 0;
    passos.forEach((tamanho, passo) => {
        // Passo par = ligado, ímpar = desligado.
        if (passo % 2 === 0) {
            for (let i = 0; i < tamanho; i++) {
                // Rampa nas bordas: sem ela cada bipe começa e termina com um estalo.
                const ganho = Math.min(1, Math.min(i, tamanho - 1 - i) / rampa);
                const t = (inicio + i) / taxa;
                saida[inicio + i] = Math.sin(2 * Math.PI * FREQUENCIA_HZ * t) * AMPLITUDE * ganho;
            }
        }
        inicio += tamanho;
    });
    return saida;
}
